import React, { useEffect, useMemo, useState } from "react";
import { FormControl, InputLabel, Select as MuiSelect, MenuItem, OutlinedInput, ListItemText, Checkbox as MuiCheckbox, Box } from "@mui/material";

type LocationOption = {
  id?: string;
  value?: string;
  label?: string;
};

type LocationProps = {
  name?: string;
  label?: string;
  options: LocationOption[] | string[]; // Accept both formats
  value?: string[]; // array of option.value or string IDs
  multiple?: boolean; // allow multiple selection
  disabled?: boolean;
  onChange?: (event: { target: { name?: string; value: string[] } }) => void;
  sx?: any;
};

const LocationSelector: React.FC<LocationProps> = ({
  name,
  label = "Standorte",
  options = [],
  value = [],
  multiple = true,
  disabled = false,
  onChange = () => {},
  sx,
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>(value || []);

  const optionByValue = useMemo(() => {
    const map = new Map<string, LocationOption>();
    // Handle both string array and object array formats
    options.forEach((option) => {
      if (typeof option === 'string') {
        // String format: use the string as both value and label (id)
        map.set(option, { id: option, value: option, label: option });
      } else {
        // Object format: use id for value and label
        const value = option.id || option.value || '';
        const label = option.id || value;
        map.set(value, { ...option, value, label });
      }
    });
    return map;
  }, [options]);

  useEffect(() => {
    if (value && value.length > 0) {
      // If parent provides specific values, use them
      setSelectedValues(value);
    } else {
      // If parent provides empty array, default to "all selected"
      setSelectedValues(Array.from(optionByValue.keys()));
    }
  }, [value, optionByValue]);

  const handleChange = (newValues: string[]) => {
    setSelectedValues(newValues);
    onChange({ target: { name, value: newValues } });
  };


  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <FormControl
        sx={{
          cursor: "pointer",
          padding: "0px",
          margin: "0px",
          backgroundColor: "white",
          m: 0,
          width: 140,
          ".MuiInputBase-root": {
            height: 25,
            padding: "0px",
            margin: "0px",
            fontSize: "12px",
            fontFamily: "Inter",
            backgroundColor: "white",
          },
          ".MuiInputBase-input": {
            backgroundColor: "white",
            marginBottom: "-5px",
            padding: "0px",
          },
          ...(sx || {}),
        }}
        size="small"
        disabled={disabled}
      >
        <InputLabel
          id={`${name || "location"}-label`}
          sx={{
            fontSize: '12px',
            fontFamily: 'Inter',
            transformOrigin: 'top left',
            // center label vertically for small input
            transform: 'translate(14px, 4px) scale(1)',
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -8px) scale(0.75)'
            }
          }}
        >
          {label || "Location"}
        </InputLabel>
        <MuiSelect
          labelId={`${name || "location"}-label`}
          multiple={multiple}
          value={selectedValues}
          onChange={(e) => {
            const val = e.target.value as string[];
            handleChange(val);
          }}
          input={<OutlinedInput label={label || "Standorte"} />}
          renderValue={(selected) => (
            Array.isArray(selected)
              ? selected
                  .filter(v => optionByValue.has(v))
                  .map(v => optionByValue.get(v)?.id || v)
                  .join(", ")
              : selected
          )}
          sx={{
            '& .MuiSelect-select': {
              textAlign: 'left',
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              paddingLeft: '8px'
            }
          }}
          MenuProps={{
            MenuListProps: {
              sx: {
                '& .MuiMenuItem-root': {
                  fontSize: 12,
                  fontFamily: 'Inter',
                  minHeight: 24, // much smaller item height
                  paddingTop: 0.5,
                  paddingBottom: 0.5,
                },
                '& .MuiListItemText-primary': {
                  fontSize: 12,
                  fontFamily: 'Inter',
                },
              },
            },
          }}
        >
          {Array.from(optionByValue.entries()).map(([value, opt]) => (
            <MenuItem key={value} value={value}>
              <MuiCheckbox 
                checked={selectedValues.indexOf(value) > -1} 
                size="small"
                sx={{ padding: '2px' }}
              />
              <ListItemText 
                primary={opt.id || value} 
                sx={{ 
                  textAlign: 'left',
                  '& .MuiTypography-root': { 
                    fontSize: 12, 
                    fontFamily: 'Inter',
                    textAlign: 'left'
                  } 
                }}
              />
            </MenuItem>
          ))}
        </MuiSelect>
      </FormControl>
    </Box>
  );
};

export default LocationSelector;